"use client"

import {
  DragDropContext,
  Draggable,
  Droppable,
  type DraggableProvided,
  type DropResult,
} from "@hello-pangea/dnd"
import { Loader2 } from "lucide-react"

import { TonePill } from "@/components/graphite/surface"
import { TINT, stageHex, stageTint } from "@/lib/stage-colors"
import {
  COLUMN_STAGE,
  COLUMN_TITLE,
  TASK_COLUMNS,
  dueLabel,
  taskSubject,
  taskTitle,
  type Board,
  type ColumnId,
  type DueTone,
} from "@/lib/tasks"
import { cn } from "@/lib/utils"
import type { Task } from "openapi/api/model"

/**
 * The Ops board, from canvas artboard 33a.
 *
 * 33a's reading of this page: the four stat cards it used to lead with said
 * less than the column headings did, so they collapse into one receipts line
 * under the title and the board gets the room. The board is the one place in
 * the product where the page's grain shows, because the cards are meant to
 * look like they are floating on something.
 *
 * Purely presentational — every figure, card and handler arrives from
 * `useOpsTasks`.
 */

/** The faint horizontal grain 33a gives the board, and nowhere else. */
const GRAIN =
  "bg-[repeating-linear-gradient(0deg,color-mix(in_srgb,var(--foreground)_2%,transparent)_0_1px,transparent_1px_6px)]"

/*
  The ramp, as an inline colour rather than an arbitrary Tailwind class.
  `text-[--stage-rejected]` is v3 shorthand and this project is on v4, so it
  emitted nothing and every date rendered in plain foreground — an overdue task
  and one due next month looked identical. `stageHex` already returns a
  `var(--token, fallback)`, which is what the rest of the app paints with.
*/
const DUE_STAGE: Record<DueTone, string | undefined> = {
  overdue: "rejected",
  today: "screening",
  quiet: undefined,
}

/** Priority borrows the ramp too: high reads as rejected, medium as screening. */
const PRIORITY_STAGE: Record<string, string> = {
  high: "rejected",
  medium: "screening",
  low: "hired",
}

/*
  No dot: a priority is one chip on a card that already carries a stage colour
  down its edge, and a second coloured mark beside it competes with that rather
  than adding anything. The ground is `TINT.ground` like every other chip in the
  app — this one had been written by hand at `TINT.pill`, nearly twice the
  strength, which is the drift that comes of copying a treatment rather than
  calling it.
*/
function PriorityPill({ priority }: { priority?: string }) {
  if (!priority) return null
  return (
    <TonePill hex={stageHex(PRIORITY_STAGE[priority] ?? "longlist")} compact dot={false}>
      {priority}
    </TonePill>
  )
}

/**
 * One card, which is both a thing you open and a thing you drag.
 *
 * Not a `<button>`. `@hello-pangea/dnd` lifts a card on Space, and Space on a
 * native button fires a click — so a keyboard user trying to move a card
 * opened its editor instead. The drag handle already carries `role="button"`
 * and a tabindex, so this is one control either way: click or Enter opens it,
 * Space picks it up.
 *
 * Wrapping a button in the handle instead would nest two controls on one card,
 * which is worse for a screen reader and ambiguous for a click.
 */
function TaskCard({
  task,
  initials,
  onOpen,
  dragging,
  drag,
}: {
  task: Task
  initials: string
  onOpen: () => void
  dragging: boolean
  drag: DraggableProvided
}) {
  const due = dueLabel(task)
  const subject = taskSubject(task)
  const teal = "var(--brand-ink, #16d0bc)"

  return (
    <div
      ref={drag.innerRef}
      {...drag.draggableProps}
      {...drag.dragHandleProps}
      onClick={onOpen}
      onKeyDown={(event) => {
        // Space belongs to the drag sensor; Enter opens the card.
        if (event.key === "Enter") {
          event.preventDefault()
          onOpen()
        }
      }}
      className={cn(
        "flex w-full cursor-pointer flex-col gap-[9px] rounded-lg border bg-card p-[13px] text-left transition-colors hover:border-foreground/20",
        dragging && "rotate-1 shadow-lg",
      )}
    >
      <span className="text-[12.5px] font-medium leading-[1.35] text-pretty">
        {taskTitle(task)}
      </span>

      {/* A closed task's description is dropped, per 33a — what is left to know
          about finished work is when it finished. */}
      {task.status !== "done" && task.description && (
        <span className="line-clamp-2 text-[11.5px] leading-[1.5] text-muted-foreground">
          {task.description}
        </span>
      )}

      <span className="flex items-center gap-[7px]">
        <PriorityPill priority={task.priority} />
        {subject && (
          <span className="min-w-0 truncate text-[11px] leading-none text-muted-foreground">
            {subject}
          </span>
        )}
      </span>

      {/* The footer exists for the avatar even when there is no date to show,
          so every card in a column ends on the same line. */}
      <span className="mt-auto flex items-center justify-between gap-2 border-t pt-[11px]">
        <span
          className="min-w-0 truncate font-mono text-[11px] leading-none text-muted-foreground"
          style={
            due && DUE_STAGE[due.tone]
              ? { color: stageHex(DUE_STAGE[due.tone] as string) }
              : undefined
          }
        >
          {due?.text ?? ""}
        </span>
        <span
          aria-hidden
          className="flex size-[22px] flex-none items-center justify-center rounded-full text-[9.5px] font-medium"
          style={{ color: teal, backgroundColor: stageTint(teal, TINT.pill) }}
        >
          {initials}
        </span>
      </span>
    </div>
  )
}

function Column({
  id,
  ids,
  byId,
  initials,
  onOpen,
}: {
  id: ColumnId
  ids: string[]
  byId: Map<string, Task>
  initials: string
  onOpen: (task: Task) => void
}) {
  const hex = stageHex(COLUMN_STAGE[id])

  return (
    <div className="flex min-h-0 flex-col gap-3">
      <div className="flex-none">
        <div className="flex items-center justify-between pb-[9px]">
          <span className="inline-flex items-center gap-2 text-[12.5px] font-medium leading-none">
            <span
              aria-hidden
              className="size-2 flex-none rounded-full"
              style={{ backgroundColor: hex }}
            />
            {COLUMN_TITLE[id]}
          </span>
          <span className="font-mono text-[11px] font-medium leading-none text-muted-foreground">
            {ids.length}
          </span>
        </div>
        {/* The column's own rule, at the strength 33a draws it. */}
        <div
          className="h-0.5 rounded-sm"
          style={{ backgroundColor: stageTint(hex, TINT.hairline) }}
        />
      </div>

      <Droppable droppableId={id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={cn(
              "flex min-h-[120px] flex-1 flex-col gap-3 rounded-lg transition-colors",
              snapshot.isDraggingOver && "bg-foreground/[0.04]",
            )}
          >
            {ids.map((taskId, index) => {
              const task = byId.get(taskId)
              if (!task) return null
              return (
                <Draggable key={taskId} draggableId={taskId} index={index}>
                  {(dragProvided, dragSnapshot) => (
                    <TaskCard
                      task={task}
                      initials={initials}
                      dragging={dragSnapshot.isDragging}
                      onOpen={() => onOpen(task)}
                      drag={dragProvided}
                    />
                  )}
                </Draggable>
              )
            })}
            {provided.placeholder}

            {/*
              33a gives Blocked a standing hint rather than an empty state,
              because it is the column you drag *into*. Its line about blocked
              "asking for a reason" is not here: a task carries no reason field,
              and a prompt for something the record cannot hold is a promise the
              product breaks on the next render.
            */}
            {id === "blocked" && (
              <p className="rounded-lg border border-dashed px-[13px] py-3 text-[11px] leading-[1.55] text-muted-foreground/70">
                Drop a card here to mark it blocked.
              </p>
            )}
          </div>
        )}
      </Droppable>
    </div>
  )
}

export function GraphiteTasksBoard({
  board,
  byId,
  initials,
  isLoading,
  isError,
  onOpenTask,
  onMove,
}: {
  board: Board
  byId: Map<string, Task>
  initials: string
  isLoading: boolean
  isError: boolean
  onOpenTask: (task: Task) => void
  onMove: (taskId: string, to: ColumnId, index: number) => void
}) {
  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result
    if (!destination) return
    /*
      Only a move between columns. Nothing stores a position within one — a
      task carries no ordering field — so a drop at another index in the same
      column wrote back the status it already had, and the next refresh
      rebuilt the board in the API's order, snapping the card back under the
      cursor. Better not to offer the move than to undo it a moment later.
    */
    if (destination.droppableId === source.droppableId) return
    onMove(draggableId, destination.droppableId as ColumnId, destination.index)
  }

  if (isLoading || isError) {
    return (
      <div className={cn("min-h-0 flex-1 px-4 py-[22px] @2xl:px-[28px]", GRAIN)}>
        {isError ? (
          <p className="text-[12.5px] text-muted-foreground">
            The board cannot be read right now.
          </p>
        ) : (
          <p className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            Reading the board…
          </p>
        )}
      </div>
    )
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      {/*
        Four columns side by side where there is room. Below @3xl they scroll
        sideways at a readable width rather than compressing to four unreadable
        slivers — the ATS pane is regularly narrower than four columns can be.
      */}
      <div
        className={cn(
          "min-h-0 flex-1 overflow-x-auto overflow-y-auto px-4 py-[22px] @2xl:px-[28px]",
          GRAIN,
        )}
      >
        <div className="grid min-w-[760px] grid-cols-4 items-start gap-[18px] @3xl:min-w-0">
          {TASK_COLUMNS.map((column) => (
            <Column
              key={column}
              id={column}
              ids={board[column]}
              byId={byId}
              initials={initials}
              onOpen={onOpenTask}
            />
          ))}
        </div>
      </div>
    </DragDropContext>
  )
}
